import type { CurrentUserResponse } from '@/modules/account-profile/types'
import type { NativeHandoffResponse } from '@/utils/mobile-auth'
import { browserLoginScope } from '@/lib/auth/browser-login-scope'
import { createLogger } from '@/lib/logger'
import { desktopAccountSession } from '@/lib/utils/native-credential-store'
import {
  desktopSecureStorage,
  getDesktopDeepLinkScheme,
  isTauri,
} from '@/lib/utils/tauri'
import { readCurrentAccountProfile } from '@/modules/account-profile/api'
import { oauthProof } from '@/utils/auth/oauth-proof.client'
import {
  mobileHandoffBody,
  rememberMobileOAuthRequest,
} from '@/utils/mobile-auth'

import type { AuthDataResponse, CreateUserDTO, User } from '@scrambl/shared'
import type { SessionSnapshot } from '@scrambl/shared/session-scope'

/**
 * Authentication API service for managing user authentication flows.
 * @module lib/api/auth.api
 */

import { apiClient, ApiError } from './api-client'
import { buildQueryString } from './base-api'
import { captureIdentityScope } from './request-scope'
import { createSingletonApi } from './singleton-factory'

const logger = createLogger('lib/api/auth.api')

function isUserData(data: unknown): data is User {
  return Boolean(
    data &&
    typeof data === 'object' &&
    'id' in data &&
    typeof data.id === 'string' &&
    data.id.trim().length > 0 &&
    'email' in data &&
    typeof data.email === 'string' &&
    data.email.trim().length > 0,
  )
}

function normalizeEmail(email: string): string {
  return email.trim().toLowerCase()
}
export interface NativeAuthDataResponse
  extends AuthDataResponse, NativeHandoffResponse {
  /** Confirmation can verify an email without establishing a login. */
  sessionEstablished?: boolean
}

function captureBrowserLogin(
  platform?: 'web' | 'mobile' | 'desktop',
  hostedHandoff = !!mobileHandoffBody().nativeHandoff,
) {
  return isTauri() || (platform && platform !== 'web') || hostedHandoff
    ? null
    : browserLoginScope.capture()
}

function acceptBrowserLogin(
  session: SessionSnapshot | null,
  response: NativeAuthDataResponse,
) {
  session?.assertCurrent()
  if (!session || response?.success !== true || response.nativeHandoff) return
  if (!isUserData(response.data))
    throw new Error('The sign-in identity could not be verified.')
  browserLoginScope.accept(session)
}

export interface ExtendedAuthDataResponse extends AuthDataResponse {
  redirect?: string
}

export type { CurrentUserResponse } from '@/modules/account-profile/types'

interface AdminAccessResponse {
  success: boolean
  data: {
    allowed: boolean
  }
}

class AuthApiService {
  cancelOAuth(state: string): Promise<void> {
    return oauthProof.cancelState(state)
  }
  /**
   * Initiates login flow with email
   * @param email - User's email address
   * @returns Auth response with session
   */
  async login(email: string): Promise<AuthDataResponse> {
    const normalizedEmail = normalizeEmail(email)
    return apiClient<AuthDataResponse>('/auth/login', {
      method: 'POST',
      body: { email: normalizedEmail },
      withAuth: false,
    })
  }

  /**
   * Starts a fresh passwordless login challenge and sends a new login code.
   * The returned session must replace the previous challenge session.
   */
  async resendLoginCode(email: string): Promise<AuthDataResponse> {
    return this.login(email)
  }

  /**
   * Verifies login with code received via email
   * @param email - User's email
   * @param code - Verification code
   * @param session - Session token
   * @param platform - Optional platform (mobile/desktop to get tokens in response)
   * @returns Auth response
   */
  async verifyLogin(
    email: string,
    code: string,
    session: string,
    platform?: 'web' | 'mobile' | 'desktop',
  ): Promise<NativeAuthDataResponse> {
    const browserSession = captureBrowserLogin(platform)
    const nativeSession = isTauri()
      ? await desktopSecureStorage.captureSession()
      : undefined
    const headers: Record<string, string> = {}
    const normalizedEmail = normalizeEmail(email)

    // For desktop, we need to request tokens like mobile does
    const effectivePlatform = platform ?? (isTauri() ? 'desktop' : 'web')
    headers['x-platform'] = effectivePlatform

    const response = await apiClient<NativeAuthDataResponse>(
      '/auth/verify-login',
      {
        method: 'POST',
        body: { email: normalizedEmail, code, session, ...mobileHandoffBody() },
        withAuth: false,
        headers,
      },
    ).catch(async error => {
      browserSession?.assertCurrent()
      if (nativeSession)
        await desktopSecureStorage.assertSessionCurrent(nativeSession)
      throw error
    })

    // If running in desktop and we got tokens, save them
    if (nativeSession)
      await desktopSecureStorage.assertSessionCurrent(nativeSession)
    if (isTauri() && response.success && response.tokens) {
      if (!isUserData(response.data))
        throw new Error('The sign-in identity could not be verified.')
      await desktopSecureStorage.saveTokens(
        {
          accessToken: response.tokens.accessToken,
          refreshToken: response.tokens.refreshToken,
          idToken: response.tokens.idToken,
        },
        {
          email: normalizedEmail,
          user: response.data,
        },
        nativeSession,
      )
    }

    acceptBrowserLogin(browserSession, response)
    return response
  }

  /**
   * Logs in with email and password.
   * @param email - User's email
   * @param password - User's password
   * @param platform - Optional platform (mobile/desktop to get tokens in response)
   * @returns Auth response
   */
  async passwordLogin(
    email: string,
    password: string,
    platform?: 'web' | 'mobile' | 'desktop',
  ): Promise<NativeAuthDataResponse> {
    const browserSession = captureBrowserLogin(platform)
    const nativeSession = isTauri()
      ? await desktopSecureStorage.captureSession()
      : undefined
    const headers: Record<string, string> = {}
    const normalizedEmail = normalizeEmail(email)
    const effectivePlatform = platform ?? (isTauri() ? 'desktop' : 'web')
    headers['x-platform'] = effectivePlatform

    const response = await apiClient<NativeAuthDataResponse>(
      '/auth/password-login',
      {
        method: 'POST',
        body: { email: normalizedEmail, password, ...mobileHandoffBody() },
        withAuth: false,
        headers,
      },
    ).catch(async error => {
      browserSession?.assertCurrent()
      if (nativeSession)
        await desktopSecureStorage.assertSessionCurrent(nativeSession)
      throw error
    })

    if (nativeSession)
      await desktopSecureStorage.assertSessionCurrent(nativeSession)
    if (isTauri() && response.success && response.tokens) {
      if (!isUserData(response.data))
        throw new Error('The sign-in identity could not be verified.')
      await desktopSecureStorage.saveTokens(
        {
          accessToken: response.tokens.accessToken,
          refreshToken: response.tokens.refreshToken,
          idToken: response.tokens.idToken,
        },
        {
          email: normalizedEmail,
          user: response.data,
        },
        nativeSession,
      )
    }

    acceptBrowserLogin(browserSession, response)
    return response
  }

  async forgotPassword(email: string): Promise<AuthDataResponse> {
    return apiClient<AuthDataResponse>('/auth/password/forgot', {
      method: 'POST',
      body: { email: normalizeEmail(email) },
      withAuth: false,
    })
  }

  async resetPassword(
    email: string,
    code: string,
    newPassword: string,
  ): Promise<AuthDataResponse> {
    return apiClient<AuthDataResponse>('/auth/password/reset', {
      method: 'POST',
      body: { email: normalizeEmail(email), code, newPassword },
      withAuth: false,
    })
  }

  /**
   * Registers a new user
   * @param userData - User registration data
   * @returns Auth response
   */
  async register(userData: CreateUserDTO): Promise<AuthDataResponse> {
    const normalizedUserData = {
      ...userData,
      email: normalizeEmail(userData.email),
    }
    return apiClient<AuthDataResponse>('/auth/register', {
      method: 'POST',
      body: normalizedUserData,
      withAuth: false,
    })
  }

  /**
   * Confirms email with verification code
   * @param email - User's email
   * @param code - Verification code
   * @param inviteToken - Optional invite token
   * @param platform - Optional platform (mobile/desktop to get tokens in response)
   * @returns Auth response
   */
  async confirmSignUp(
    email: string,
    code: string,
    inviteToken?: string,
    platform?: 'web' | 'mobile' | 'desktop',
    registrationSession?: string,
  ): Promise<NativeAuthDataResponse> {
    const browserSession = captureBrowserLogin(platform)
    const nativeSession = isTauri()
      ? await desktopSecureStorage.captureSession()
      : undefined
    const headers: Record<string, string> = {}
    const normalizedEmail = normalizeEmail(email)
    const effectivePlatform = platform ?? (isTauri() ? 'desktop' : undefined)
    if (effectivePlatform) {
      headers['x-platform'] = effectivePlatform
    }

    const response = await apiClient<NativeAuthDataResponse>('/auth/confirm', {
      method: 'POST',
      body: {
        email: normalizedEmail,
        code,
        inviteToken,
        registrationSession,
        ...mobileHandoffBody(),
      },
      withAuth: false,
      headers,
    }).catch(async error => {
      browserSession?.assertCurrent()
      if (nativeSession)
        await desktopSecureStorage.assertSessionCurrent(nativeSession)
      throw error
    })

    if (nativeSession)
      await desktopSecureStorage.assertSessionCurrent(nativeSession)
    if (isTauri() && response.success && response.tokens) {
      if (!isUserData(response.data))
        throw new Error('The sign-in identity could not be verified.')
      await desktopSecureStorage.saveTokens(
        {
          accessToken: response.tokens.accessToken,
          refreshToken: response.tokens.refreshToken,
          idToken: response.tokens.idToken,
        },
        {
          email: normalizedEmail,
          user: response.data,
        },
        nativeSession,
      )
    }

    browserSession?.assertCurrent()
    if (response.sessionEstablished === true)
      acceptBrowserLogin(browserSession, response)
    return response
  }

  /**
   * Resends verification code
   * @param email - User's email
   * @returns Auth response
   */
  async resendConfirmationCode(email: string): Promise<AuthDataResponse> {
    const normalizedEmail = normalizeEmail(email)
    return apiClient<AuthDataResponse>('/auth/resend-code', {
      method: 'POST',
      body: { email: normalizedEmail },
      withAuth: false,
    })
  }

  /**
   * Gets current authenticated user profile
   * @returns Current user data
   */
  getCurrentUser(): Promise<CurrentUserResponse> {
    return readCurrentAccountProfile()
  }

  async getAdminAccessStatus(): Promise<boolean> {
    try {
      const response = await apiClient<AdminAccessResponse>(
        '/auth/admin/access',
        {
          method: 'GET',
          withAuth: true,
        },
      )

      return Boolean(response.data?.allowed)
    } catch (error) {
      logger.warn('[AuthApi] Admin access check failed:', error)
      return false
    }
  }

  /**
   * Logs out the current user
   * @returns Auth response with redirect
   */
  async logout(): Promise<ExtendedAuthDataResponse> {
    const identity = captureIdentityScope()
    const credentials = isTauri() ? desktopAccountSession.capture() : null
    const response = await apiClient<AuthDataResponse>('/auth/logout', {
      method: 'POST',
      withAuth: true,
      skipOrgContext: true,
      silentAuthFailure: true,
    })
    identity.assertCurrent()
    credentials?.assertCurrent()
    // The transport synthesizes { success: true } for empty/invalid JSON. That
    // is not confirmation that the server committed the session revocation.
    if (
      response?.success !== true ||
      typeof response.message !== 'string' ||
      !response.message.trim()
    ) {
      throw new Error('Sign out could not be confirmed. Please try again.')
    }
    return { ...response, redirect: '/login' }
  }

  /**
   * Checks if an email exists
   * @param email - Email to check
   * @param purpose - Purpose of check (login or register)
   * @param inviteToken - Optional invite token
   * @returns Whether email exists
   */
  async checkEmailExists(
    email: string,
    purpose?: 'login' | 'register',
    inviteToken?: string,
  ): Promise<{ exists: boolean }> {
    const queryString = buildQueryString({
      email: normalizeEmail(email),
      purpose,
      inviteToken,
    })
    return apiClient<{ exists: boolean }>(
      `/auth/check-email-exists${queryString}`,
      {
        method: 'GET',
        withAuth: false,
      },
    )
  }

  /**
   * Checks if email exists for registration flow
   * @param email - Email to check
   * @param inviteToken - Optional invite token
   * @returns Whether email exists
   */
  async checkEmailExistsForRegistration(
    email: string,
    inviteToken?: string,
  ): Promise<{ exists: boolean }> {
    return this.checkEmailExists(email, 'register', inviteToken)
  }

  /**
   * Refreshes the authentication token
   * @returns Auth response
   */
  async refreshToken(): Promise<ExtendedAuthDataResponse> {
    let nativeSession: SessionSnapshot | undefined
    try {
      nativeSession = isTauri()
        ? await desktopSecureStorage.captureSession()
        : undefined
      const desktopAccountId = nativeSession?.identity
      // For desktop, include refresh token in header
      const headers: Record<string, string> = {}
      if (isTauri()) {
        if (!desktopAccountId)
          throw new Error('No active desktop session to refresh.')
        const tokens = await desktopSecureStorage.getTokens()
        if (!tokens?.refreshToken)
          throw new Error('No desktop refresh token is available.')
        headers['Authorization'] = `Bearer ${tokens.refreshToken}`
        headers['x-platform'] = 'desktop'
      }

      if (nativeSession)
        await desktopSecureStorage.assertSessionCurrent(nativeSession)
      const response = await apiClient<AuthDataResponse>('/auth/refresh', {
        method: 'POST',
        withAuth: true,
        headers,
      })

      // If running in desktop and we got new tokens, save them
      if (nativeSession)
        await desktopSecureStorage.assertSessionCurrent(nativeSession)
      if (isTauri() && response.success && response.tokens) {
        await desktopSecureStorage.saveTokens(
          {
            accessToken: response.tokens.accessToken,
            refreshToken: response.tokens.refreshToken,
            idToken: response.tokens.idToken,
          },
          {
            user: isUserData(response.data) ? response.data : undefined,
            expectedActiveAccountId: desktopAccountId ?? undefined,
          },
          nativeSession,
        )
      }

      return response
    } catch (error) {
      if (nativeSession)
        await desktopSecureStorage.assertSessionCurrent(nativeSession)
      logger.error('[AuthApi] Token refresh error:', error)
      return {
        success: false,
        message: 'Failed to refresh token',
        redirect: '/login',
      }
    }
  }

  /**
   * Gets a short-lived token for socket authentication
   * @returns Socket token
   */
  async getSocketToken(): Promise<{ token: string }> {
    return apiClient<{ token: string }>('/auth/socket-token', {
      method: 'GET',
      withAuth: true,
    })
  }

  /**
   * Gets Google OAuth login URL
   * @param inviteToken - Optional invite token
   * @param flow - Login or register flow
   * @param platform - Platform making the request
   * @param deepLinkScheme - Deep link scheme for desktop
   * @param forceNewAccount - Force showing account picker for adding new accounts
   * @returns Auth response with URL
   */
  private async federatedLoginUrl(
    provider: 'google' | 'apple' | 'microsoft',
    params: {
      inviteToken?: string
      flow?: 'login' | 'register'
      accountType?: 'personal' | 'work'
      forceNewAccount?: string
    },
    platform?: 'web' | 'mobile' | 'desktop',
    deepLinkScheme?: string,
  ): Promise<AuthDataResponse> {
    const hostedRequest = mobileHandoffBody().nativeHandoff
    if (isTauri()) await desktopSecureStorage.captureSession()
    const proof = await oauthProof.prepare()
    try {
      const effectivePlatform = platform ?? (isTauri() ? 'desktop' : undefined)
      const effectiveScheme =
        deepLinkScheme ??
        (isTauri() ? await getDesktopDeepLinkScheme() : undefined)
      const headers: Record<string, string> = {}
      if (effectivePlatform) headers['x-platform'] = effectivePlatform
      if (effectiveScheme) headers['x-deep-link-scheme'] = effectiveScheme
      const query = buildQueryString({
        ...params,
        codeChallenge: proof.challenge,
      })
      const response = await apiClient<AuthDataResponse>(
        `/auth/${provider}/login${query}`,
        {
          method: 'GET',
          withAuth: false,
          headers,
        },
      )
      if (
        !response.success ||
        !response.data ||
        !('authUrl' in response.data) ||
        typeof response.data.authUrl !== 'string'
      )
        throw new Error('Could not start sign-in. Please try again.')
      if (hostedRequest) {
        const current = mobileHandoffBody().nativeHandoff
        if (
          current?.state !== hostedRequest.state ||
          current.codeChallenge !== hostedRequest.codeChallenge
        )
          throw new Error(
            'This device sign-in changed. Start signing in again.',
          )
      }
      await oauthProof.bind(proof.id, response.data.authUrl)
      if (effectivePlatform === 'mobile')
        rememberMobileOAuthRequest(response.data.authUrl)
      return response
    } catch (error) {
      await oauthProof.cancel(proof.id)
      throw error
    }
  }

  async getGoogleLoginUrl(
    inviteToken?: string,
    flow?: 'login' | 'register',
    platform?: 'web' | 'mobile' | 'desktop',
    deepLinkScheme?: string,
    forceNewAccount?: boolean,
  ): Promise<AuthDataResponse> {
    return this.federatedLoginUrl(
      'google',
      {
        inviteToken,
        flow,
        forceNewAccount: forceNewAccount ? 'true' : undefined,
      },
      platform,
      deepLinkScheme,
    )
  }

  async getAppleLoginUrl(
    inviteToken?: string,
    flow?: 'login' | 'register',
    platform?: 'web' | 'mobile' | 'desktop',
    deepLinkScheme?: string,
    forceNewAccount?: boolean,
  ): Promise<AuthDataResponse> {
    return this.federatedLoginUrl(
      'apple',
      {
        inviteToken,
        flow,
        forceNewAccount: forceNewAccount ? 'true' : undefined,
      },
      platform,
      deepLinkScheme,
    )
  }

  /**
   * Handles Google OAuth callback
   * @param code - OAuth code
   * @param state - OAuth state
   * @param platform - Optional platform (mobile/desktop to get tokens in response)
   * @returns Auth response
   */
  async handleGoogleCallback(
    code: string,
    state: string,
    platform?: 'web' | 'mobile' | 'desktop',
  ): Promise<NativeAuthDataResponse> {
    const handoffBody = mobileHandoffBody()
    const browserSession = captureBrowserLogin(
      platform,
      !!handoffBody.nativeHandoff,
    )
    const { result: response, session } = await oauthProof.exchange(
      state,
      (codeVerifier, original) => {
        original.assertCurrent()
        return apiClient<NativeAuthDataResponse>('/auth/oauth/exchange', {
          method: 'POST',
          withAuth: false,
          body: { code, state, codeVerifier, ...handoffBody },
          headers: handoffBody.nativeHandoff
            ? { 'x-platform': 'mobile' }
            : undefined,
        })
      },
    )
    if (isTauri() && response.success && response.tokens) {
      if (!isUserData(response.data))
        throw new Error('The sign-in identity could not be verified.')
      session.assertCurrent()
      await desktopSecureStorage.saveTokens(
        response.tokens,
        { user: response.data },
        session,
      )
    }
    // Receipt ownership/cleanup finishes before advancing this login's revision.
    acceptBrowserLogin(browserSession, response)
    return response
  }

  /** Gets a Microsoft authorization URL bound to this client's private proof. */
  async getMicrosoftLoginUrl(
    accountType: 'personal' | 'work' = 'personal',
    inviteToken?: string,
    flow?: 'login' | 'register',
    platform?: 'web' | 'mobile' | 'desktop',
    deepLinkScheme?: string,
  ): Promise<AuthDataResponse> {
    return this.federatedLoginUrl(
      'microsoft',
      { accountType, inviteToken, flow },
      platform,
      deepLinkScheme,
    )
  }

  /**
   * Handles Microsoft OAuth callback
   * The Cognito code exchange is provider-agnostic, so this mirrors the Google
   * callback handling (same /auth/callback endpoint + desktop token storage).
   * @param code - OAuth code
   * @param state - OAuth state
   * @param platform - Optional platform (mobile/desktop to get tokens in response)
   * @returns Auth response
   */
  async handleMicrosoftCallback(
    code: string,
    state: string,
    platform?: 'web' | 'mobile' | 'desktop',
  ): Promise<AuthDataResponse> {
    return this.handleGoogleCallback(code, state, platform)
  }

  /** Cognito's callback exchange is provider-agnostic. */
  async handleAppleCallback(
    code: string,
    state: string,
    platform?: 'web' | 'mobile' | 'desktop',
  ): Promise<AuthDataResponse> {
    return this.handleGoogleCallback(code, state, platform)
  }

  /**
   * Links existing account with Google
   * @param googleIdToken - Google ID token
   * @returns Auth response
   */
  async linkGoogleAccount(googleIdToken: string): Promise<AuthDataResponse> {
    return apiClient<AuthDataResponse>('/auth/google/link', {
      method: 'POST',
      body: { googleIdToken },
      withAuth: true,
    })
  }
}

export const authApi = createSingletonApi(AuthApiService)
